import { Autocomplete, type SxProps, TextField, type TextFieldPropsSizeOverrides, type Theme } from '@mui/material';
import { type OverridableStringUnion } from '@mui/types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useContext } from 'react';

import CurrentUserContext from '@/Contexts/CurrentUser';
import { generateTimeOptions } from '@/utils/dateTimeHelpers';

dayjs.extend(customParseFormat);

interface TimeAutocompleteProps {
  time: string;
  onTimeChange: (newTime: string) => void;
  label?: string;
  size?: OverridableStringUnion<'small' | 'medium', TextFieldPropsSizeOverrides>;
  error?: boolean;
  helperText?: string;
  sx?: SxProps<Theme> | undefined;
  /** When set, restricts the dropdown list (freeSolo typing is unchanged). */
  options?: string[];
  formatOptionLabel?: (option: string) => string;
  disabled?: boolean;
}

const TimeAutocomplete: React.FC<TimeAutocompleteProps> = ({
  time,
  onTimeChange,
  label,
  size = 'small',
  error,
  helperText,
  options: optionsFromProps,
  formatOptionLabel,
  disabled = false,
  sx,
}) => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const selectOptions = optionsFromProps ?? generateTimeOptions();

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      options={selectOptions}
      getOptionLabel={(option) =>
        formatOptionLabel ? formatOptionLabel(option) : dayjs(option, 'HH:mm').format(dateTimePreference.time)
      }
      disableClearable
      fullWidth
      // Ensure value is never null to prevent uncontrolled input warnings
      defaultValue={time || ''}
      // Handle selection from the dropdown
      onChange={(_, newValue) => {
        onTimeChange((newValue as string) || '');
      }}
      // Handle manual typing
      onInputChange={(_, newInputValue) => {
        onTimeChange(newInputValue || '');
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          fullWidth
          disabled={disabled}
          error={error || time.trim() === ''}
          helperText={helperText}
        />
      )}
      sx={{ ...(sx ? sx : { mt: 2 }) }}
    />
  );
};

export default TimeAutocomplete;
